import { useState } from 'react';
import { HotelConnectionSharing } from './HotelConnectionSharing';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Chip,
  Stack,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { connectionAccessCodes, validConnectionAccess } from './hotelConnectionAccess';
import { client } from '@frontend/features/common/services';

import { useProviderNames } from '../hotel-search-policy/useProviderNames';

interface Connection {
  connector_account_id: string;
  display_name: string;
  status: 'active' | 'disabled';
  bookings_enabled?: boolean;
  configuration: {
    search: {
      client: string;
      test_mode: boolean;
      timeout_millis: number;
      access: { mode: string; access_codes?: string[] };
    };
  };
}

export function HotelConnections({ agencyId }: { agencyId: number }) {
  const { t } = useTranslation();
  const cache = useQueryClient();
  const suppliers = useProviderNames();
  const key = ['hotel-connections', agencyId];
  const path = `agencies/${agencyId}/hotels/connector-accounts`;
  const accounts = useQuery(key, async () => (await client.get<{ data: Connection[] }>(path)).data.data, {
    enabled: Number.isSafeInteger(agencyId) && agencyId > 0,
  });
  const [open, setOpen] = useState(false);
  const [confirmBookings, setConfirmBookings] = useState<Connection | null>(null);
  const [label, setLabel] = useState('');
  const [clientName, setClientName] = useState('');
  const [access, setAccess] = useState('');
  const [secret, setSecret] = useState('');
  const [testMode, setTestMode] = useState(true);
  const [expanded, setExpanded] = useState<string | null>(null);
  const create = useMutation(
    async () => {
      try {
        await client.post(path, {
          connector: 'travelgate',
          display_name: label.trim(),
          configuration: {
            search: {
              client: clientName.trim(),
              test_mode: testMode,
              timeout_millis: 25000,
              access: {
                mode: 'include',
                access_codes: connectionAccessCodes(access),
              },
            },
          },
          credentials: { api_key: secret },
        });
      } catch {
        throw new Error('Hotel connection failed');
      }
    },
    {
      onSuccess: () => {
        setOpen(false);
        setSecret('');
        setLabel('');
        setAccess('');
        cache.invalidateQueries(key);
        cache.invalidateQueries(['hotel-search-policy', agencyId]);
      },
      // Axios errors carry the request body. Do not retain a credential-bearing
      // error in global query state or render its provider message.
    },
  );
  const toggle = useMutation(
    async (account: Connection) =>
      client.patch(`${path}/${encodeURIComponent(account.connector_account_id)}`, {
        status: account.status === 'active' ? 'disabled' : 'active',
      }),
    {
      onSuccess: async () => {
        await Promise.all([
          cache.invalidateQueries(key),
          cache.invalidateQueries(['hotel-search-policy', agencyId]),
          cache.invalidateQueries(['hotel-booking-permissions', agencyId]),
        ]);
      },
    },
  );
  const bookingPermission = useMutation(
    async ({ account, enabled }: { account: Connection; enabled: boolean }) => {
      await client.patch(`${path}/${encodeURIComponent(account.connector_account_id)}`, { bookings_enabled: enabled });
    },
    {
      onSuccess: async () => {
        setConfirmBookings(null);
        await Promise.all([
          cache.invalidateQueries(key),
          cache.invalidateQueries(['hotel-search-policy', agencyId]),
          cache.invalidateQueries(['hotel-booking-permissions', agencyId]),
        ]);
      },
    },
  );
  const close = () => {
    if (!create.isLoading) {
      setOpen(false);
      setSecret('');
      create.reset();
    }
  };
  return (
    <Box display="flex" flexDirection="column" gap={2}>
      <Stack direction="row" flexWrap="wrap" gap={1} justifyContent="space-between" alignItems="center">
        <Typography variant="h6">{t('hotelConnections.title')}</Typography>
        <Button
          variant="outlined"
          disabled={!Number.isSafeInteger(agencyId) || agencyId <= 0 || accounts.isLoading || accounts.isError}
          onClick={() => {
            create.reset();
            setOpen(true);
          }}
        >
          {t('hotelConnections.add')}
        </Button>
      </Stack>
      <Typography variant="body2">{t('hotelConnections.hint')}</Typography>
      {accounts.isLoading && <Typography>{t('hotelConnections.loading')}</Typography>}
      {(accounts.isError || toggle.isError || bookingPermission.isError) && (
        <Alert severity="error">{t('hotelConnections.error')}</Alert>
      )}
      {accounts.isError && <Button onClick={() => accounts.refetch()}>{t('hotelConnections.retry')}</Button>}
      {accounts.isSuccess && accounts.data?.length === 0 && (
        <Alert severity="info">{t('hotelConnections.empty')}</Alert>
      )}
      {accounts.data?.map((account) => (
        <Box
          key={account.connector_account_id}
          sx={{ p: 2, border: 1, borderColor: 'divider', borderRadius: 2, minWidth: 0 }}
        >
          <Typography variant="h6" sx={{ overflowWrap: 'anywhere' }}>
            {account.display_name}
          </Typography>
          <Chip
            size="small"
            label={t(account.configuration.search.test_mode ? 'hotelConnections.test' : 'hotelConnections.live')}
            sx={{ mb: 1 }}
          />
          <Stack direction="row" flexWrap="wrap" gap={2}>
            <FormControlLabel
              label={t('hotelSettings.connectionActive')}
              control={
                <Switch
                  checked={account.status === 'active'}
                  disabled={toggle.isLoading || bookingPermission.isLoading}
                  onChange={() => toggle.mutate(account)}
                />
              }
            />
            <FormControlLabel
              label={t('hotelConnections.bookings')}
              control={
                <Switch
                  checked={account.bookings_enabled === true}
                  disabled={bookingPermission.isLoading || toggle.isLoading || accounts.isFetching || accounts.isError}
                  onChange={(_, enabled) => {
                    bookingPermission.reset();
                    if (enabled) setConfirmBookings(account);
                    else bookingPermission.mutate({ account, enabled: false });
                  }}
                />
              }
            />
          </Stack>
          <Button
            onClick={() => setExpanded(expanded === account.connector_account_id ? null : account.connector_account_id)}
            aria-expanded={expanded === account.connector_account_id}
          >
            {t('hotelSettings.accesses', { count: account.configuration.search.access.access_codes?.length ?? 0 })}
          </Button>
          <Box hidden={expanded !== account.connector_account_id} sx={{ minWidth: 0 }}>
            {(account.configuration.search.access.access_codes ?? []).map((code) => (
              <Box key={code} sx={{ py: 1, borderBottom: 1, borderColor: 'divider', overflowWrap: 'anywhere' }}>
                <Typography>{suppliers.names.get(code) ?? t('hotelPolicy.unknownProvider')}</Typography>
                <Typography variant="caption" color="text.secondary">
                  Travelgate · {account.display_name} · {code}
                </Typography>
              </Box>
            ))}
            {account.configuration.search.access.mode !== 'include' && (
              <Typography>{t('hotelSettings.dynamicAccess')}</Typography>
            )}
            {suppliers.isError && (
              <Alert severity="info">
                {t('hotelPolicy.namesUnavailable')}{' '}
                <Button onClick={() => suppliers.refetch()}>{t('hotelPolicy.retry')}</Button>
              </Alert>
            )}
          </Box>
          <Box mt={2} pt={2} sx={{ borderTop: 1, borderColor: 'divider' }}>
            <Typography variant="body2" color="text.secondary" mb={1}>
              {t('hotelSharing.hint')}
            </Typography>
            <HotelConnectionSharing
              agencyId={agencyId}
              connectionId={account.connector_account_id}
              name={account.display_name}
            />
          </Box>
        </Box>
      ))}
      <Dialog
        open={!!confirmBookings}
        onClose={() => !bookingPermission.isLoading && setConfirmBookings(null)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>{t('hotelConnections.bookings')}</DialogTitle>
        <DialogContent>
          <Typography>{confirmBookings?.display_name}</Typography>
          <Alert severity="warning">{t('hotelConnections.commercialWarning')}</Alert>
          {bookingPermission.isError && <Alert severity="error">{t('hotelConnections.error')}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button disabled={bookingPermission.isLoading} onClick={() => setConfirmBookings(null)}>
            {t('hotelConnections.close')}
          </Button>
          <Button
            disabled={bookingPermission.isLoading || accounts.isError}
            onClick={() => confirmBookings && bookingPermission.mutate({ account: confirmBookings, enabled: true })}
          >
            {t('hotelConnections.confirmBookings')}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={open} onClose={close} fullWidth maxWidth="sm">
        <DialogTitle>{t('hotelConnections.add')}</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} pt={1}>
            <TextField
              label={t('hotelConnections.name')}
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              inputProps={{ maxLength: 150 }}
            />
            <TextField
              label={t('hotelConnections.client')}
              value={clientName}
              onChange={(e) => setClientName(e.target.value)}
            />
            <TextField
              label={t('hotelConnections.access')}
              helperText={t(
                access.trim() && !validConnectionAccess(access)
                  ? 'hotelConnections.invalidAccess'
                  : 'hotelConnections.accessHint',
              )}
              error={Boolean(access.trim()) && !validConnectionAccess(access)}
              value={access}
              onChange={(e) => setAccess(e.target.value)}
            />
            <TextField
              type="password"
              autoComplete="new-password"
              label={t('hotelConnections.key')}
              value={secret}
              onChange={(e) => setSecret(e.target.value)}
            />
            <FormControlLabel
              label={t('hotelConnections.test')}
              control={<Switch checked={testMode} onChange={(e) => setTestMode(e.target.checked)} />}
            />
            <Alert severity="info">{t('hotelConnections.safety')}</Alert>
            {create.isError && <Alert severity="error">{t('hotelConnections.error')}</Alert>}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={close} disabled={create.isLoading}>
            {t('hotelConnections.close')}
          </Button>
          <Button
            disabled={
              create.isLoading ||
              !label.trim() ||
              !clientName.trim() ||
              !validConnectionAccess(access) ||
              !secret.trim()
            }
            onClick={() => create.mutate()}
          >
            {t('hotelConnections.save')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
